import { Add, Close } from '@mui/icons-material'
import { Box, Button, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel, Grid2, IconButton, InputLabel, MenuItem, Radio, RadioGroup, Select, SelectChangeEvent, Step, StepButton, Stepper, styled, TextField, Typography } from '@mui/material'
import React, { Dispatch, memo, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ServiceCard from '../../../components/Cards/ServiceCard'
import { IBranch, IField, IStep, ITranslation } from 'sierra'
import { useApp } from '../../../contexts/AppContext'
import { ArcherContainer, ArcherElement } from 'react-archer'
import { useAuth } from '../../../hooks/useAuth'
import api from '../../../utils/axios'
import { isTranslation } from '../../../utils/isTranslation'

const placeholderBranch = {
  branch_id: 0,
  branch_name: { en: 'Branch Name', ar: 'اسم الفرع' },
  branch_header_url: 'https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png',
  branch_logo_url: 'https://www.svgrepo.com/show/508699/landscape-placeholder.svg',
  created_by: 1,
  branch_managers: [],
  creation_timestamp: new Date('2025-01-01'),
  is_deleted: false
}

const emptyTranslation = { en: '', ar: '', zh: '' }

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});


const ServiceStep = memo((
  { serviceName,
    setServiceName,
    serviceDescription,
    setServiceDescription,
    servicePrice,
    setServicePrice,
    servicePerHead,
    setServicePerHead,
    serviceHeaderImage,
    setServiceHeaderImage,
    selectedBranch,
    setSelectedBranch,
    lang,
    branches,
    readUploadFile
  }: {
    serviceName: ITranslation,
    setServiceName: Dispatch<SetStateAction<ITranslation>>,
    serviceDescription: ITranslation,
    setServiceDescription: Dispatch<SetStateAction<ITranslation>>,
    servicePrice: number,
    setServicePrice: Dispatch<SetStateAction<number>>,
    servicePerHead: boolean,
    setServicePerHead: Dispatch<SetStateAction<boolean>>,
    serviceHeaderImage: string | null | undefined,
    setServiceHeaderImage: Dispatch<SetStateAction<string | null | undefined>>,
    selectedBranch: number,
    setSelectedBranch: Dispatch<SetStateAction<number>>,
    lang: 'en' | 'ar' | 'zh',
    branches: IBranch[],
    readUploadFile: Function
  }
) => {
  const { translate } = useApp()

  useEffect(() => {
    localStorage.setItem('serviceName', JSON.stringify(serviceName));
  }, [serviceName])

  useEffect(() => {
    localStorage.setItem('selectedBranch', JSON.stringify(selectedBranch));
  }, [selectedBranch])

  useEffect(() => {
    localStorage.setItem('serviceDescription', JSON.stringify(serviceDescription));
  }, [serviceDescription])

  useEffect(() => {
    if (serviceHeaderImage !== undefined) localStorage.setItem('serviceHeaderImage', JSON.stringify(serviceHeaderImage));
  }, [serviceHeaderImage])

  useEffect(() => {
    localStorage.setItem('servicePrice', JSON.stringify(servicePrice));
  }, [servicePrice])

  useEffect(() => {
    localStorage.setItem('servicePerHead', JSON.stringify(servicePerHead));
  }, [servicePerHead])


  return (
    <Grid2 container size={'grow'} flexDirection={'column'} flexWrap={'nowrap'} sx={{ overflowX: 'hidden', overflowY: 'auto' }} paddingX={2} spacing={4}>

      <Grid2>
        <ServiceCard
          branches={branches}
          service={{
            service_id: 0,
            branch_id: 0,
            created_by: 0,
            service_name: serviceName[lang] ? serviceName : { en: 'Service Name', ar: 'اسم الخدمة' },
            service_desc: serviceDescription[lang] ? serviceDescription : { en: 'Service Description', ar: 'تفاصيل الخدمة' },
            service_price: servicePrice !== 0 ? servicePrice : 0,
            is_per_head: servicePerHead ? servicePerHead : false,
            is_deleted: false,
            creation_timestamp: new Date(),
            service_header_url: serviceHeaderImage || 'https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png',
            service_version: 0
          }}
          branch={selectedBranch === -1 ? placeholderBranch : branches.find(b => b.branch_id === selectedBranch)}
          hideButtons={true}
        />
      </Grid2>

      <Grid2>
        <FormControl fullWidth>
          <InputLabel>{translate("Select Branch")} *</InputLabel>
          <Select
            required
            variant='filled'
            value={selectedBranch === -1 ? '' : String(selectedBranch)}
            size='small'
            onChange={(e: SelectChangeEvent) => setSelectedBranch(Number(e.target.value as string))}
          >
            {branches.map((b: IBranch, i: number) => <MenuItem key={i} value={b.branch_id}>{b.branch_name[lang]}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid2>

      <Grid2>
        <TextField
          required
          fullWidth
          label={translate('Service Name')}
          variant={'outlined'}
          size='small'
          sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
          value={serviceName[lang]}
          onChange={e => setServiceName(b => ({ ...b, [lang]: e.target.value }))}
        />
      </Grid2>

      <Grid2 size={'grow'}>
        <Button component="label" fullWidth variant='outlined' sx={{ borderRadius: 2, fontWeight: 'bold', borderWidth: '2px' }}>
          {serviceHeaderImage ? translate('Edit Header Image') : `${translate("Upload Header Image")} +`}
          <VisuallyHiddenInput
            accept="image/*" // Limit to only accept image files
            required
            type="file"
            onChange={e => readUploadFile(e, setServiceHeaderImage)} />
        </Button>
      </Grid2>

      <Grid2>
        <TextField
          required
          fullWidth
          label={translate('Service Description')}
          multiline
          rows={8}
          variant={'outlined'}
          size='small'
          sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
          value={serviceDescription[lang]}
          onChange={e => setServiceDescription(b => ({ ...b, [lang]: e.target.value }))}
        />
      </Grid2>

      <Grid2 container display={'flex'} alignItems={'center'} spacing={4} flexDirection={{ xs: 'column', sm: 'row' }}>

        <Grid2 size={'grow'}>
          <TextField
            required
            fullWidth
            label={translate('Service Price')}
            variant={'outlined'}
            type='number'
            size='small'
            sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
            value={(servicePrice / 100).toString() || ''}
            onChange={(e) => setServicePrice((parseInt(e.target.value.replace(/^0+/, '')) * 100) > 0 ? (parseInt(e.target.value.replace(/^0+/, '')) * 100) : 0)}
          />
        </Grid2>

        <Grid2 size={'grow'}>
          <RadioGroup
            value={servicePerHead}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setServicePerHead(event.target.value === "true")}
          >
            <FormControlLabel value="true" control={<Radio />} label={translate("Variable Price per Head")} />
            <FormControlLabel value="false" control={<Radio />} label={translate("Full Price")} />
          </RadioGroup>
        </Grid2>

      </Grid2>

    </Grid2>
  )
})


const FormStep = memo(({ form, setForm, lang }: { form: IStep[], setForm: Dispatch<SetStateAction<IStep[]>>, lang: 'en' | 'ar' | 'zh' }) => {
  const { translate } = useApp()

  useEffect(() => {
    console.log(form);

  }, [form])


  return (
    <Grid2 container size={'grow'} flexDirection={'column'} flexWrap={'nowrap'} sx={{ overflowX: 'hidden', overflowY: 'auto' }} paddingX={2} spacing={4}>

      <Grid2 container display={'flex'} alignItems={'center'}>
        <Grid2>
          <Typography variant='h6' fontWeight={'bold'} sx={{ color: t => t.palette.primary.main }}>{translate("Steps")}</Typography>
        </Grid2>
        <Grid2 size={'grow'} height={'1px'} bgcolor={'grey'} />
        <Grid2>
          <IconButton onClick={() => setForm(prevForm => [...prevForm, { step_name: emptyTranslation }])}>
            <Add />
          </IconButton>
        </Grid2>
      </Grid2>

      <Grid2 container flexDirection={'column'} spacing={4}>
        {form[0] &&
          form.map((s, si) =>

            <StepDetails key={si} {...{ s, si, form, setForm, lang }} />
          )
        }
      </Grid2 >

    </Grid2 >
  )
})


const StepDetails = memo(({ s, si, form, setForm, lang }: { s: IStep, si: number, form: IStep[], setForm: Dispatch<SetStateAction<IStep[]>>, lang: 'en' | 'ar' | 'zh' }) => {
  const { translate, screenSize } = useApp()

  const [confirmDelete, setConfirmDelete] = useState<boolean>(false)

  const handleStepTranslation = useCallback((key: keyof IStep, value: string, index: number) => {
    setForm(prevForm => {
      return prevForm.map((step, i) => {
        if (i !== index) return step; // Return unchanged step

        const existingTranslation = (step[key] as ITranslation) || {}; // Ensure it's an object

        return {
          ...step,
          [key]: { ...existingTranslation, [lang]: value },
        };
      });
    });
  }, [lang]); // Add `lang` to the dependency array

  const handleAddToFields = useCallback((i: number) => {
    setForm(prevForm => {
      const newSteps = [...prevForm];
      const step = newSteps[i];

      const newFields = step.fields ? [...step.fields] : [];

      newFields.push({
        field_type: 'text',
        field_required: false,
        field_placeholder: undefined,
        field_multiline: 1,
        field_label: emptyTranslation,
        field_desc: emptyTranslation,
        field_options: [],
      });

      newSteps[i] = {
        ...step,
        fields: newFields,
      };
      return newSteps;
    })
  }, [])

  const handleFieldTranslation = useCallback((key: keyof IField, value: string, stepIndex: number, fieldIndex: number) => {
    setForm(prevForm => {
      const newSteps = [...prevForm];

      if (!newSteps[stepIndex].fields) newSteps[stepIndex].fields = [];
      const newFields = [...newSteps[stepIndex].fields];

      newFields[fieldIndex] = {
        ...newFields[fieldIndex],
        [key]: { ...newFields[fieldIndex][key] as ITranslation, [lang]: value },
      };

      newSteps[stepIndex] = {
        ...newSteps[stepIndex],
        fields: newFields,
      };

      return newSteps;
    })
  }, [lang])

  const handleFieldValue = useCallback((key: keyof IField, value: any, stepIndex: number, fieldIndex: number) => {
    setForm(prevForm => {
      const newSteps = [...prevForm];

      if (!newSteps[stepIndex].fields) newSteps[stepIndex].fields = [];
      const newFields = [...newSteps[stepIndex].fields];

      newFields[fieldIndex] = {
        ...newFields[fieldIndex],
        [key]: value,
      };

      if (key === 'field_type' && ['text', 'number', 'file'].includes(value)) newFields[fieldIndex] = { ...newFields[fieldIndex], field_options: [], };

      newSteps[stepIndex] = {
        ...newSteps[stepIndex],
        fields: newFields,
      };

      return newSteps;
    })
  }, [])

  const handleAddToOptions = useCallback((stepIndex: number, fieldIndex: number) => {
    setForm(prevForm => {
      const newSteps = [...prevForm];
      const step = newSteps[stepIndex];

      const newFields = step.fields ? [...step.fields] : [];
      if (!newFields[fieldIndex].field_options) newFields[fieldIndex].field_options = []

      newFields[fieldIndex].field_options.push(emptyTranslation);

      newSteps[stepIndex] = {
        ...step,
        fields: newFields,
      };
      return newSteps;
    })
  }, [])

  const handleOptionTranslation = useCallback(
    (value: string, stepIndex: number, fieldIndex: number, optionIndex: number) => {
      setForm(prevForm => {
        // Clone the steps array
        const newSteps = [...prevForm];

        if (!newSteps[stepIndex].fields) newSteps[stepIndex].fields = [];
        const newFields = [...newSteps[stepIndex].fields];

        if (!newFields[fieldIndex].field_options) newFields[fieldIndex].field_options = [];

        // Clone the field_options array
        const newOptions = [...newFields[fieldIndex].field_options];

        // Clone the specific option object to avoid modifying previous state directly
        newOptions[optionIndex] = {
          ...newOptions[optionIndex],
          [lang]: value
        };

        // Update the fields array with the new options
        newFields[fieldIndex] = {
          ...newFields[fieldIndex],
          field_options: newOptions,
        };

        // Update the steps array with the new fields
        newSteps[stepIndex] = {
          ...newSteps[stepIndex],
          fields: newFields,
        };

        return newSteps;
      });
    },
    [lang]
  );

  return (
    <Grid2 container flexDirection={'column'} padding={{ xs: 0, sm: 2 }} spacing={4}>

      {si > 0 &&
        <Grid2 height={'1px'} bgcolor={'grey'} />}
      <ArcherContainer endMarker={false} strokeColor='brown'>
        <Grid2 container display={'flex'} alignItems={'center'} spacing={2}>
          <Grid2>
            <ArcherElement id='root' relations={
              form[si].fields && form[si].fields.length > 0
                ? form[si].fields
                  .filter((f, fi) => !!f) // Ensure field exists
                  .map((f, fi) => ({
                    targetId: `element${fi}`,
                    targetAnchor: lang === 'ar' ? 'right' : 'left',
                    sourceAnchor: !confirmDelete ? 'bottom' : lang === 'ar' ? 'right' : 'left',
                  }))
                : []
            }>
              <Grid2>
                {!confirmDelete ?
                  <IconButton color="error" sx={{ backgroundColor: 'rgba(255, 0, 0, 0.1)', border: '1px solid red' }} onClick={() => setConfirmDelete(true)}>
                    <Close />
                  </IconButton>
                  :
                  <Grid2 container display={'flex'} alignItems={'end'}>
                    <Grid2>
                      <Button color='error' variant='contained' endIcon={<Close />} onClick={() => {
                        setForm(prevForm => prevForm.filter((_, index) => si !== index))
                        setConfirmDelete(false)
                      }}>
                        {translate("Are you sure?")}
                      </Button>
                    </Grid2>
                    <Grid2>
                      <Button size='small' onClick={() => setConfirmDelete(false)}>{translate("Cancel")}</Button>
                    </Grid2>
                  </Grid2>
                }
              </Grid2>
            </ArcherElement>

          </Grid2>
          <Grid2>
            <TextField
              required
              size='small'
              label={translate('Step Name')}
              value={s.step_name[lang]}
              onChange={e => handleStepTranslation('step_name', e.target.value, si)}
              sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', width: { xs: 120, sm: 200 } }}
            />
          </Grid2>
          <Grid2 size={'grow'} height={'1px'} bgcolor={'grey'} />
          <Grid2>
            {screenSize.xs ? <Add color='primary' onClick={() => handleAddToFields(si)} /> :
              <Button endIcon={<Add />} onClick={() => handleAddToFields(si)}>{translate('Add Field')}</Button>}
          </Grid2>
        </Grid2>

        <Grid2 container flexDirection={'column'} spacing={4} paddingTop={4}>
          {form[si].fields && form[si].fields[0] && form[si].fields.map((f, fi) =>

            <Grid2 container key={fi} flexDirection={'column'} paddingLeft={lang === 'ar' ? { xs: 0, sm: 4 } : 8} paddingRight={lang === 'ar' ? 8 : { xs: 0, sm: 4 }}>
              {fi > 0 && <Grid2 height={'1px'} bgcolor={'grey'} />}
              <ArcherElement id={`element${fi}`}>
                <Grid2 container flexDirection={'column'} spacing={2} bgcolor={'rgba(255,255,255,0.3)'} padding={2} borderRadius={2} border={'1px brown solid'}>
                  <Grid2 container display={'flex'} alignItems={'center'} flexDirection={{ xs: 'column', sm: 'row' }}>
                    <Grid2 size={'grow'}>
                      <TextField
                        required
                        size='small'
                        label={translate('Field Label')}
                        value={f.field_label[lang]}
                        onChange={e => handleFieldTranslation('field_label' as keyof IField, e.target.value, si, fi)}
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
                      />
                    </Grid2>

                    <Grid2 size={'grow'}>
                      <FormControl fullWidth>
                        <InputLabel>{translate("Field Type")} *</InputLabel>
                        <Select
                          size='small'
                          value={f.field_type || ''}
                          label="Field Type"
                          onChange={e => handleFieldValue('field_type', e.target.value, si, fi)}
                        >
                          <MenuItem value={'text'}>{translate("Text")}</MenuItem>
                          <MenuItem value={'number'}>{translate("Number")}</MenuItem>
                          <MenuItem value={'file'}>{translate("File")}</MenuItem>
                          <MenuItem value={'select'}>{translate("Select")}</MenuItem>
                          <MenuItem value={'multiselect'}>{translate("Multi-Select")}</MenuItem>
                          <MenuItem value={'date'}>{translate("Date")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid2>

                    <Grid2 >
                      <TextField
                        required
                        fullWidth
                        type='number'
                        size='small'
                        label={translate('Lines')}
                        value={f.field_multiline}
                        onChange={e => handleFieldValue('field_multiline', parseInt(e.target.value) >= 1 ? e.target.value : 1, si, fi)}
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', width: 70 }}
                      />
                    </Grid2>

                    <Grid2 size={'grow'}>
                      <FormControlLabel required control={<Checkbox checked={f.field_required} onChange={e => handleFieldValue('field_required', e.target.checked, si, fi)} />} label={translate("Required")} />
                    </Grid2>

                    <Grid2>
                      <IconButton onClick={() => setForm(prevForm => prevForm.map((s, sIndex) => ({ ...s, fields: s.fields?.filter((f, fIndex) => si !== sIndex || fi !== fIndex) })))} color='error'>
                        <Close />
                      </IconButton>
                    </Grid2>
                  </Grid2>

                  {f.field_options && f.field_options[0] &&
                    f.field_options.map((o, oi) =>
                      <Grid2 container key={oi}>
                        <Grid2 size={'grow'}>
                          <TextField
                            fullWidth
                            required
                            size='small'
                            label={`${translate("Option")} ${oi + 1}`}
                            value={o[lang]}
                            onChange={e => handleOptionTranslation(e.target.value, si, fi, oi)}
                            sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
                          />
                        </Grid2>
                        <Grid2>
                          <IconButton onClick={() => setForm(prevForm => prevForm.map((s, sIndex) => ({ ...s, fields: s.fields?.map((f, fIndex) => ({ ...f, field_options: f.field_options?.filter((o, oIndex) => si !== sIndex || fi !== fIndex || oi !== oIndex) })) })))} color='error'>
                            <Close />
                          </IconButton>
                        </Grid2>
                      </Grid2>
                    )
                  }

                  {(f.field_type === 'select' || f.field_type === 'multiselect') &&
                    <Grid2>
                      <Button fullWidth variant='contained' size='small' onClick={() => handleAddToOptions(si, fi)}>{translate("Add Options")} *</Button>
                    </Grid2>}

                  <Grid2>
                    <TextField
                      fullWidth
                      required
                      size='small'
                      label={translate('Field Description')}
                      multiline
                      rows={4}
                      value={f.field_desc[lang]}
                      onChange={e => handleFieldTranslation('field_desc' as keyof IField, e.target.value, si, fi)}
                      sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
                    />
                  </Grid2>
                  <Grid2 container spacing={2}>
                    <Grid2 size={'grow'}>
                      <TextField
                        fullWidth
                        size='small'
                        label={translate('Field Placeholder')}
                        value={f.field_placeholder ? f.field_placeholder[lang] : ''}
                        onChange={e => handleFieldTranslation('field_placeholder' as keyof IField, e.target.value, si, fi)}
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
                      />
                    </Grid2>
                  </Grid2>
                </Grid2>
              </ArcherElement>

            </Grid2>


          )}
        </Grid2>
      </ArcherContainer>
    </Grid2 >)
})


const ServiceCreate = memo(() => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { lang, translate, screenSize } = useApp()
  const branches = location.state.branches

  const [activeStep, setActiveStep] = useState<number>(0);

  const [serviceName, setServiceName] = useState<ITranslation>(JSON.parse(localStorage.getItem('serviceName') || '""') || emptyTranslation)
  const [serviceDescription, setServiceDescription] = useState<ITranslation>(JSON.parse(localStorage.getItem('serviceDescription') || '""') || emptyTranslation)
  const [servicePrice, setServicePrice] = useState<number>(JSON.parse(localStorage.getItem('servicePrice') || '""') || 0)
  const [servicePerHead, setServicePerHead] = useState<boolean>(JSON.parse(localStorage.getItem('servicePerHead') || '""') || false)
  const [serviceHeaderImage, setServiceHeaderImage] = useState<string | null | undefined>(JSON.parse(localStorage.getItem('serviceHeaderImage') || '""') || undefined)
  const [selectedBranch, setSelectedBranch] = useState<number>(JSON.parse(localStorage.getItem('selectedBranch') || '""') || -1)
  const [form, setForm] = useState<IStep[]>(JSON.parse(localStorage.getItem('form') || '""') || [])
  const [apiCall, setApiCall] = useState<boolean>(false)
  const [clearConfirm, setClearConfirm] = useState<boolean>(false)


  const fileInputRef = useRef<HTMLInputElement>(null);

  const readUploadFile = useCallback((e: React.ChangeEvent<HTMLInputElement>, setStateFunction: Dispatch<SetStateAction<string | null | undefined>>) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onload = (e) => {
        const data = e.target?.result as string | null | undefined;
        setStateFunction(data);
      };
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [])

  const steps = [
    {
      name: `${translate("Service Details")} *`,
      component: <ServiceStep key={0}  {...{
        serviceName,
        setServiceName,
        serviceDescription,
        setServiceDescription,
        servicePrice,
        setServicePrice,
        servicePerHead,
        setServicePerHead,
        serviceHeaderImage,
        setServiceHeaderImage,
        selectedBranch,
        setSelectedBranch,
        lang,
        branches,
        readUploadFile
      }} />,
      isComplete: (): boolean => (
        isTranslation(serviceName)
          && isTranslation(serviceDescription)
          && servicePrice > 0
          && serviceHeaderImage
          && selectedBranch !== -1 ? true : false)
    },
    {
      name: `${translate("Form Details")} *`, component: <FormStep key={1} {...{ form, setForm, lang }} />, isComplete: (): boolean => (
        form[0]
          && form.every(s => isTranslation(s.step_name))
          && form.every(s => s.fields && s.fields[0])
          && form.every(s => s.fields && s.fields.every(f => isTranslation(f.field_label)))
          && form.every(s => s.fields && s.fields.every(f => isTranslation(f.field_desc)))
          && form.every(s => s.fields && s.fields.every(f => f.field_type && ['text', 'number', 'file', 'select', 'multiselect', 'date'].includes(f.field_type)))
          && form.every(s => s.fields && s.fields.every(f => ['select', 'multiselect'].includes(f.field_type) ? (f.field_options && f.field_options[0] && f.field_options.every(o => isTranslation(o))) : true))
          ? true : false)
    }
  ];

  const handleNext = () => {
    const newActiveStep = steps.findIndex((step, i) => (i > activeStep || activeStep == steps.length - 1) && !step.isComplete())
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const clearForm = useCallback(() => {
    const keys = ['serviceName', 'serviceDescription', 'selectedBranch', 'serviceHeaderImage', 'servicePrice', 'servicePerHead', 'form']
    keys.forEach((key: string) => localStorage.removeItem(key))
    setServiceName(emptyTranslation)
    setServiceHeaderImage(undefined)
    setServiceDescription(emptyTranslation)
    setServicePrice(0)
    setServicePerHead(false)
    setSelectedBranch(-1)
    setForm([])
  }, [])

  const handleCreateService = useCallback(() => {
    setApiCall(true)
    api.post('services', {
      created_by: user?.user_id,
      branch_id: selectedBranch,
      service_name: serviceName,
      service_header_image: serviceHeaderImage,
      service_desc: serviceDescription,
      service_price: servicePrice,
      is_per_head: servicePerHead,
      steps: form.map(f => ({
        ...f, fields: f.fields?.map(field => ({
          ...field,
          field_placeholder: field.field_placeholder && isTranslation(field.field_placeholder) ? field.field_placeholder : undefined,
          field_options: field.field_options?.every(option => isTranslation(option)) ? field.field_options : undefined
        }))
      }))
    }, { meta: { showSnackbar: true } }).then(() => {
      setApiCall(false)
      clearForm()
    }).catch((err) => {
      console.log(err);
      setApiCall(false)
      // clearForm()
    })
  }, [serviceName, serviceHeaderImage, selectedBranch, serviceDescription, servicePrice, servicePerHead, form, user, clearForm])

  useEffect(() => {
    localStorage.setItem('form', JSON.stringify(form));
  }, [form])

  useEffect(() => {
    console.log(form);
  }, [form])


  return (
    <Grid2 container size={'grow'} height={'100%'} padding={2} flexDirection={'column'} spacing={4}>

      <Grid2 container size={'auto'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>

        <Grid2>
          <Typography variant='h5'>
            {translate("Create Service")}
          </Typography>
        </Grid2>

        <Grid2>
          <IconButton onClick={() => navigate('/system/services')}>
            <Close />
          </IconButton>
        </Grid2>

      </Grid2>


      <Grid2 container size={'grow'} flexDirection={'column'} gap={3}>
        <Grid2>
          <Stepper connector={screenSize.xs && lang === 'ar' ? null : undefined} orientation={screenSize.xs ? 'vertical' : 'horizontal'} nonLinear activeStep={activeStep}>
            {steps.map((step, index) => (
              <Step key={index} completed={step.isComplete()}
                sx={{
                  '& .MuiStepLabel-root .Mui-completed': {
                    color: 'green', // circle color (COMPLETED)
                  },
                  '& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel': {
                    color: 'grey.500', // text label (COMPLETED)
                  },
                  '& .MuiStepLabel-root .Mui-active': {
                    color: 'primary.main', // circle color (ACTIVE)
                  },
                  '& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel': {
                    color: 'common.white', // text label (ACTIVE)
                  },
                  '& .MuiStepLabel-root .Mui-active .MuiStepIcon-text': {
                    fill: 'black', // circle's number (ACTIVE)
                  },
                }}
              >
                <StepButton color={"inherit"} onClick={handleStep(index)}>
                  {step.name}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Grid2>
        {steps.map((step, index) => index == activeStep ? step.component : null)}
        <Grid2 marginTop={'auto'} flexDirection={{ xs: 'column', sm: 'row' }}>
          <Box sx={{ display: 'flex', pt: 2 }} flexDirection={{ xs: 'column', sm: 'row' }}>
            {!clearConfirm ? <Button color='error' onClick={() => setClearConfirm(true)} endIcon={<Close />}>
              {translate("Clear Data")}
            </Button> :
              <Grid2 container display={'flex'} alignItems={'end'}>
                <Grid2>
                  <Button color='error' variant='contained' onClick={() => {
                    clearForm()
                    setClearConfirm(false)
                  }} endIcon={<Close />}>
                    {translate("Are you sure?")}
                  </Button>
                </Grid2>
                <Grid2>
                  <Button size='small' onClick={() => setClearConfirm(false)}>{translate("Cancel")}</Button>
                </Grid2>
              </Grid2>
            }
            <Button
              color={"inherit"}
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              {translate("Previous step")}
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button onClick={handleNext} sx={{ mr: 1 }} disabled={steps.every(step => step.isComplete())}>
              {translate("Next step")}
            </Button>
            <Button variant='contained' onClick={handleCreateService} disabled={steps.some(step => !step.isComplete()) || apiCall} endIcon={apiCall ? <CircularProgress /> : null} >
              {translate("Create Service")}
            </Button>
          </Box>
        </Grid2>
      </Grid2>


    </Grid2>
  )
})

export default ServiceCreate
